import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { authApi, legalDocumentsApi } from '../../assets/api';
import { CurrentUserContext } from '../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../assets/contexts/translationContext';
import { useTelegram } from '../../assets/hooks/useTelegram';
import { useHapticPress } from '../../assets/hooks/useHapticPress';
import MiniPreloader from '../MiniPreloader/MiniPreloader';
import BottomSheet from '../shared/BottomSheet/BottomSheet';
import LegalDocumentPopup from '../OnBoardingPages/OnboardingTerms/LegalDocumentPopup';
import './LegalDocumentsUpdateSheet.css';

const TickIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      fill="currentColor"
      d="M8.37 18.275a1 1 0 0 0 1.337-.068l10.5-10.5a1 1 0 1 0-1.414-1.414L9 16.086l-3.293-3.293a1 1 0 1 0-1.414 1.414l4 4z"
    />
  </svg>
);

function getLocalizedValue(value, locale = 'en') {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  const localizedValue = value[locale] ?? value.en;

  if (typeof localizedValue === 'string') {
    return localizedValue;
  }

  return Object.values(value).find((item) => typeof item === 'string') || '';
}

function normalizeActiveDocumentsResponse(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
}

function normalizeVersion(version) {
  const numericVersion = Number(version);
  return Number.isInteger(numericVersion) ? numericVersion : null;
}

function getDocumentKey(document) {
  return document?.slug || document?._id || '';
}

function getDocumentTitle(document, locale, fallbackTitle) {
  return getLocalizedValue(document?.title, locale) || document?.slug || fallbackTitle;
}

function getSignedDocumentsSignature(currentUser) {
  return (Array.isArray(currentUser?.signed_legal_documents)
    ? currentUser.signed_legal_documents
    : [])
    .filter((document) => document?.slug)
    .map((document) => `${document.slug}:${normalizeVersion(document.version) ?? 'unknown'}`)
    .sort()
    .join('|');
}

function getSignedDocumentsMap(signature) {
  return new Map(
    String(signature || '')
      .split('|')
      .filter(Boolean)
      .map((item) => {
        const separatorIndex = item.lastIndexOf(':');
        const slug = separatorIndex >= 0 ? item.slice(0, separatorIndex) : item;
        const rawVersion = separatorIndex >= 0 ? item.slice(separatorIndex + 1) : '';
        return [slug, rawVersion === 'unknown' ? null : normalizeVersion(rawVersion)];
      })
  );
}

function buildSignedDocuments(documents) {
  return documents
    .map((document) => ({
      slug: document?.slug,
      version: normalizeVersion(document?.version),
    }))
    .filter((document) => document.slug && document.version !== null);
}

function getDocumentsToSign(documents, signedDocumentsMap) {
  return documents.filter((document) => {
    const slug = document?.slug;
    const activeVersion = normalizeVersion(document?.version);

    if (!slug || activeVersion === null) {
      return false;
    }

    const signedVersion = signedDocumentsMap.get(slug);

    return signedVersion === null
      || typeof signedVersion === 'undefined'
      || signedVersion < activeVersion;
  });
}

function stringifyApiErrorValue(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyApiErrorValue(item))
      .filter(Boolean)
      .join('\n');
  }

  if (typeof value === 'object') {
    return (
      stringifyApiErrorValue(value.msg)
      || stringifyApiErrorValue(value.message)
      || stringifyApiErrorValue(value.detail)
    );
  }

  return String(value);
}

function getApiErrorMessage(error, fallback) {
  return (
    stringifyApiErrorValue(error?.detail)
    || stringifyApiErrorValue(error?.payload?.detail)
    || stringifyApiErrorValue(error?.payload)
    || stringifyApiErrorValue(error?.message)
    || fallback
  );
}

function shouldSkipForPath(pathname) {
  return pathname.startsWith('/onboarding')
    || pathname === '/unlock'
    || pathname === '/forgot-pin';
}

function LegalDocumentsUpdateSheet() {
  const location = useLocation();
  const { webApp } = useTelegram();
  const hapticProps = useHapticPress(webApp);
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const {
    common,
    documents: documentsTranslation,
    language,
    t,
  } = useContext(TranslationContext);
  const [documents, setDocuments] = useState([]);
  const [signedBySlug, setSignedBySlug] = useState({});
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [hasCheckedDocuments, setHasCheckedDocuments] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [exactDocumentsBySlug, setExactDocumentsBySlug] = useState({});
  const [isExactDocumentLoading, setIsExactDocumentLoading] = useState(false);
  const [exactDocumentError, setExactDocumentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const shouldCheckDocuments = Boolean(currentUser?._id)
    && !shouldSkipForPath(location.pathname);
  const signedDocumentsSignature = useMemo(
    () => getSignedDocumentsSignature(currentUser),
    [currentUser]
  );
  const signedDocumentsMap = useMemo(
    () => getSignedDocumentsMap(signedDocumentsSignature),
    [signedDocumentsSignature]
  );
  const documentsToSign = useMemo(
    () => getDocumentsToSign(documents, signedDocumentsMap),
    [documents, signedDocumentsMap]
  );
  const signedDocumentsPayload = useMemo(() => buildSignedDocuments(documents), [documents]);
  const allChecked = documentsToSign.length > 0
    && documentsToSign.every((document) => signedBySlug[document.slug]);
  const canSubmit = allChecked
    && signedDocumentsPayload.length > 0
    && !isDocumentsLoading
    && !isSubmitting;
  const selectedSlug = selectedDocument?.slug;
  const popupDocument = selectedSlug
    ? exactDocumentsBySlug[selectedSlug] || selectedDocument
    : selectedDocument;
  const hasDocumentUpdates = hasCheckedDocuments && documentsToSign.length > 0;
  const isVisible = shouldCheckDocuments
    && !isAccepted
    && hasDocumentUpdates;

  const loadDocuments = useCallback(async () => {
    if (!shouldCheckDocuments) {
      return;
    }

    setIsDocumentsLoading(true);
    setDocumentsError('');
    setIsAccepted(false);

    try {
      const response = await legalDocumentsApi.getActive();
      const nextDocuments = normalizeActiveDocumentsResponse(response);
      const nextDocumentsToSign = getDocumentsToSign(nextDocuments, signedDocumentsMap);

      setDocuments(nextDocuments);
      setSignedBySlug(
        Object.fromEntries(
          nextDocumentsToSign
            .filter((document) => document?.slug)
            .map((document) => [document.slug, false])
        )
      );
    } catch (error) {
      const message = getApiErrorMessage(error, documentsTranslation.loadDocumentsFailed);

      setDocumentsError(message);
      toast.error(message);
    } finally {
      setHasCheckedDocuments(true);
      setIsDocumentsLoading(false);
    }
  }, [documentsTranslation.loadDocumentsFailed, shouldCheckDocuments, signedDocumentsMap]);

  useEffect(() => {
    setDocuments([]);
    setSignedBySlug({});
    setDocumentsError('');
    setHasCheckedDocuments(false);
    setSelectedDocument(null);
    setExactDocumentsBySlug({});
    setExactDocumentError('');
    setIsAccepted(false);

    if (shouldCheckDocuments) {
      loadDocuments();
    }
  }, [currentUser?._id, loadDocuments, shouldCheckDocuments, signedDocumentsSignature]);

  const loadExactDocument = useCallback(async (document) => {
    if (!document?.slug || exactDocumentsBySlug[document.slug]) {
      return;
    }

    setIsExactDocumentLoading(true);
    setExactDocumentError('');

    try {
      const nextDocument = await legalDocumentsApi.getExact({ slug: document.slug });

      setExactDocumentsBySlug((prevDocuments) => ({
        ...prevDocuments,
        [document.slug]: nextDocument,
      }));
    } catch (error) {
      const message = getApiErrorMessage(error, documentsTranslation.loadDocumentFailed);

      setExactDocumentError(message);
      toast.error(message);
    } finally {
      setIsExactDocumentLoading(false);
    }
  }, [documentsTranslation.loadDocumentFailed, exactDocumentsBySlug]);

  const openDocument = (document) => {
    setSelectedDocument(document);
    setExactDocumentError('');
    loadExactDocument(document);
  };

  const closeDocument = () => {
    setSelectedDocument(null);
    setExactDocumentError('');
  };

  const toggleDocument = (document) => {
    if (!document?.slug) {
      return;
    }

    setSignedBySlug((prevSignedBySlug) => ({
      ...prevSignedBySlug,
      [document.slug]: !prevSignedBySlug[document.slug],
    }));
  };

  const handleDocumentKeyDown = (event, document) => {
    hapticProps.onKeyDown?.(event);

    if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    toggleDocument(document);
  };

  const handleReadClick = (event, document) => {
    event.preventDefault();
    event.stopPropagation();
    openDocument(document);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      const nextCurrentUser = await authApi.updateSignedDocuments({
        signedDocuments: signedDocumentsPayload,
      });

      setCurrentUser(nextCurrentUser);
      setIsAccepted(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, documentsTranslation.submitFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <BottomSheet
        className="legal-documents-update-sheet"
        panelClassName="legal-documents-update-sheet__panel"
        title={documentsTranslation.updateTitle}
        titleId="legal-documents-update-title"
        isDismissible={false}
      >
        <p className="legal-documents-update-sheet__subtitle">
          {documentsTranslation.updateText}
        </p>

        {isDocumentsLoading ? (
          <MiniPreloader
            className="legal-documents-update-sheet__loader"
            label={documentsTranslation.loadingDocuments}
          />
        ) : documentsError ? (
          <div className="legal-documents-update-sheet__empty" role="alert">
            <p>{documentsError}</p>
            <button
              type="button"
              className="legal-documents-update-sheet__retry"
              onClick={loadDocuments}
              {...hapticProps}
            >
              {common.tryAgain}
            </button>
          </div>
        ) : (
          <ul className="legal-documents-update-sheet__list">
            {documentsToSign.map((document) => {
              const key = getDocumentKey(document);
              const isSigned = Boolean(signedBySlug[document.slug]);
              const title = getDocumentTitle(
                document,
                language,
                documentsTranslation.legalDocument
              );

              return (
                <li key={key} className="legal-documents-update-sheet__item">
                  <div
                    className={`legal-documents-update-sheet__row${
                      isSigned ? ' legal-documents-update-sheet__row--checked' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-label={t('documents.agreeToDocument', { title })}
                    aria-pressed={isSigned}
                    onClick={() => toggleDocument(document)}
                    {...hapticProps}
                    onKeyDown={(event) => handleDocumentKeyDown(event, document)}
                  >
                    <span className="legal-documents-update-sheet__copy">
                      <span className="legal-documents-update-sheet__document-title">
                        {title}
                      </span>
                      <span className="legal-documents-update-sheet__document-actions">
                        {normalizeVersion(document.version) !== null && (
                          <span className="legal-documents-update-sheet__document-meta">
                            {documentsTranslation.version.replace(
                              '{{version}}',
                              normalizeVersion(document.version)
                            )}
                          </span>
                        )}
                        <button
                          type="button"
                          className="legal-documents-update-sheet__read"
                          onClick={(event) => handleReadClick(event, document)}
                          {...hapticProps}
                        >
                          {common.read}
                        </button>
                      </span>
                    </span>
                    <span
                      className={`legal-documents-update-sheet__checkbox${
                        isSigned ? ' legal-documents-update-sheet__checkbox--checked' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {isSigned && <TickIcon className="legal-documents-update-sheet__check" />}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <button
          type="button"
          className="legal-documents-update-sheet__submit"
          disabled={!canSubmit}
          onClick={handleSubmit}
          {...hapticProps}
        >
          {isSubmitting ? documentsTranslation.submitting : documentsTranslation.acceptUpdates}
        </button>
      </BottomSheet>

      <AnimatePresence>
        {selectedDocument && (
          <LegalDocumentPopup
            key={selectedDocument.slug || selectedDocument._id || 'document'}
            document={popupDocument}
            error={exactDocumentError}
            isLoading={isExactDocumentLoading}
            locale={language}
            onClose={closeDocument}
            onRetry={() => loadExactDocument(selectedDocument)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default LegalDocumentsUpdateSheet;
